/**
 * Parse / export reservoir contour as GeoJSON.
 * Coordinates in the file are either:
 *   - geographic: [longitude, latitude] (standard GeoJSON), or
 *   - local km: [x_km, y_km] relative to the layer datum (contour_origin).
 * Callers decide which via explicit user choice on import/export.
 */

export type ContourPoint = { x: number; y: number };
export type ContourCoordFormat = 'geographic' | 'local_km';

function ringToPoints(ring: unknown): ContourPoint[] {
  if (!Array.isArray(ring) || ring.length < 3) return [];
  const pts: ContourPoint[] = [];
  for (const c of ring) {
    if (!Array.isArray(c) || c.length < 2) continue;
    const x = Number(c[0]);
    const y = Number(c[1]);
    if (Number.isFinite(x) && Number.isFinite(y)) pts.push({ x, y });
  }
  if (pts.length >= 2) {
    const a = pts[0];
    const b = pts[pts.length - 1];
    if (Math.abs(a.x - b.x) < 1e-9 && Math.abs(a.y - b.y) < 1e-9) pts.pop();
  }
  return pts;
}

function geomToPoints(geom: { type?: string; coordinates?: unknown; geometries?: unknown[] } | null): ContourPoint[] {
  if (!geom || typeof geom !== 'object') return [];
  const t = geom.type;
  if (t === 'Polygon') {
    const coords = geom.coordinates as unknown[][] | undefined;
    return ringToPoints(coords?.[0] || []);
  }
  if (t === 'MultiPolygon') {
    const polys = (geom.coordinates as unknown[][][]) || [];
    let best: ContourPoint[] = [];
    for (const poly of polys) {
      const pts = ringToPoints(poly?.[0] || []);
      if (pts.length > best.length) best = pts;
    }
    return best;
  }
  if (t === 'LineString') {
    return ringToPoints(geom.coordinates || []);
  }
  if (t === 'GeometryCollection') {
    for (const g of geom.geometries || []) {
      const pts = geomToPoints(g as { type?: string; coordinates?: unknown });
      if (pts.length >= 3) return pts;
    }
  }
  return [];
}

/** GeoJSON → raw [{x,y}, …] as stored in the file (lon/lat or x_km/y_km). */
export function contourFromGeoJSON(data: unknown): ContourPoint[] {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid GeoJSON');
  }
  const obj = data as {
    type?: string;
    features?: { geometry?: { type?: string; coordinates?: unknown } }[];
    geometry?: { type?: string; coordinates?: unknown };
  };
  if (obj.type === 'FeatureCollection') {
    for (const f of obj.features || []) {
      const pts = geomToPoints(f.geometry || null);
      if (pts.length >= 3) return pts;
    }
    throw new Error('No Polygon/LineString with ≥3 points in FeatureCollection');
  }
  if (obj.type === 'Feature') {
    const pts = geomToPoints(obj.geometry || null);
    if (pts.length < 3) throw new Error('Feature geometry needs ≥3 points');
    return pts;
  }
  const pts = geomToPoints(obj);
  if (pts.length < 3) throw new Error('Geometry needs ≥3 points');
  return pts;
}

/** [{x,y}] → GeoJSON Feature Polygon (x/y as provided; annotate CRS). */
export function contourToGeoJSON(
  contour: ContourPoint[],
  name = 'reservoir_contour',
  format: ContourCoordFormat = 'local_km',
) {
  const ring = (contour || []).map((p) => [p.x, p.y]);
  if (ring.length) {
    const a = ring[0];
    const b = ring[ring.length - 1];
    if (a[0] !== b[0] || a[1] !== b[1]) ring.push([a[0], a[1]]);
  }
  const crs_note =
    format === 'geographic'
      ? 'coordinates are [longitude, latitude] degrees'
      : 'local km relative to contour_origin (same as well x_km / y_km)';
  return {
    type: 'Feature' as const,
    properties: { name, crs_note, coord_format: format },
    geometry: { type: 'Polygon' as const, coordinates: [ring] },
  };
}

export function downloadGeoJSON(filename: string, obj: unknown) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/geo+json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}
